import { Box, FormLabel, Input, SimpleGrid, Text, Flex } from '@chakra-ui/react'
import React, { useState, useEffect } from 'react'

interface RootCanalsSectionProps {
  currentRootCanals: string
  setCurrentRootCanals: (value: string) => void
  rootCanalsTotal: string
  setRootCanalsTotal: (value: string) => void
  firstRootCanalAge: string
  setFirstRootCanalAge: (value: string) => void
  lastRootCanalAge: string
  setLastRootCanalAge: (value: string) => void
  computedRootYearsExposed: string | number
  computedRootCanalExposure: string | number
  rootCanalError: boolean
  isSaved: boolean
  adjustedRootCanals?: string
  adjustedTotalRootCanals?: string
  hasRootCanalAdjustments?: boolean
  hasTotalRootCanalAdjustments?: boolean
}

const RootCanalsSection: React.FC<RootCanalsSectionProps> = ({
  currentRootCanals,
  setCurrentRootCanals,
  rootCanalsTotal,
  setRootCanalsTotal,
  firstRootCanalAge,
  setFirstRootCanalAge,
  lastRootCanalAge,
  setLastRootCanalAge,
  computedRootYearsExposed,
  computedRootCanalExposure,
  rootCanalError,
  isSaved,
  adjustedRootCanals,
  adjustedTotalRootCanals,
  hasRootCanalAdjustments,
  hasTotalRootCanalAdjustments,
}) => {
  // Helper function to determine if a field should be disabled
  // Only disable fields when the form has been saved AND the field has a value
  const isFieldDisabled = (value: string) => {
    return isSaved && value !== ''
  }

  // Internal state for handling input changes
  const [workingCurrentRootCanals, setWorkingCurrentRootCanals] =
    useState(currentRootCanals)
  const [workingRootCanalsTotal, setWorkingRootCanalsTotal] =
    useState(rootCanalsTotal)
  const [workingFirstRootCanalAge, setWorkingFirstRootCanalAge] =
    useState(firstRootCanalAge)
  const [workingLastRootCanalAge, setWorkingLastRootCanalAge] =
    useState(lastRootCanalAge)

  // Update internal state when props change (for initial load and adjustments)
  useEffect(() => {
    if (isSaved && adjustedRootCanals) {
      setWorkingCurrentRootCanals(adjustedRootCanals)
    } else {
      setWorkingCurrentRootCanals(currentRootCanals)
    }
  }, [isSaved, currentRootCanals, adjustedRootCanals])

  useEffect(() => {
    if (isSaved && adjustedTotalRootCanals) {
      setWorkingRootCanalsTotal(adjustedTotalRootCanals)
    } else {
      setWorkingRootCanalsTotal(rootCanalsTotal)
    }
  }, [isSaved, rootCanalsTotal, adjustedTotalRootCanals])

  useEffect(() => {
    setWorkingFirstRootCanalAge(firstRootCanalAge)
  }, [firstRootCanalAge])

  useEffect(() => {
    setWorkingLastRootCanalAge(lastRootCanalAge)
  }, [lastRootCanalAge])

  return (
    <>
      <Text fontSize="lg" fontWeight="bold" mt={8}>
        Number of Root Canals
      </Text>
      <SimpleGrid columns={1} spacing={4} mt={4}>
        <Flex>
          <FormLabel w="50%">1. Current number of root canals</FormLabel>
          <Box w="25%">
            <Input
              type="number"
              placeholder="Enter current number"
              value={workingCurrentRootCanals}
              onChange={(e) => {
                if (!isFieldDisabled(currentRootCanals)) {
                  setWorkingCurrentRootCanals(e.target.value)
                }
              }}
              onBlur={() => setCurrentRootCanals(workingCurrentRootCanals)}
              disabled={isFieldDisabled(currentRootCanals)}
            />
            {isSaved && hasRootCanalAdjustments && (
              <Text fontSize="xs" color="blue.600" mt={1}>
                (Adjusted based on dental procedures)
              </Text>
            )}
          </Box>
        </Flex>
        <Flex>
          <FormLabel w="50%">2. Number of root canals in your life</FormLabel>
          <Box w="25%">
            <Input
              type="number"
              placeholder="Enter total number"
              value={workingRootCanalsTotal}
              onChange={(e) => {
                if (!isFieldDisabled(rootCanalsTotal)) {
                  setWorkingRootCanalsTotal(e.target.value)
                }
              }}
              onBlur={() => setRootCanalsTotal(workingRootCanalsTotal)}
              disabled={isFieldDisabled(rootCanalsTotal)}
            />
            {isSaved && hasTotalRootCanalAdjustments && (
              <Text fontSize="xs" color="blue.600" mt={1}>
                (Adjusted based on dental procedures)
              </Text>
            )}
          </Box>
        </Flex>
        <Flex>
          <FormLabel w="50%">
            3. Estimated age you had 1st root canal placed
          </FormLabel>
          <Input
            w="25%"
            type="number"
            placeholder="Enter age"
            value={workingFirstRootCanalAge}
            onChange={(e) => {
              if (!isFieldDisabled(firstRootCanalAge)) {
                setWorkingFirstRootCanalAge(e.target.value)
              }
            }}
            onBlur={() => setFirstRootCanalAge(workingFirstRootCanalAge)}
            borderColor={rootCanalError ? 'red' : undefined}
            borderWidth={rootCanalError ? '2px' : undefined}
            disabled={isFieldDisabled(firstRootCanalAge)}
          />
        </Flex>
        <Flex>
          <FormLabel w="50%">
            4. Age you had your root canal removed (if still have root canals
            leave blank)
          </FormLabel>
          <Input
            w="25%"
            type="number"
            placeholder="Enter age"
            value={workingLastRootCanalAge}
            onChange={(e) => {
              if (!isFieldDisabled(lastRootCanalAge)) {
                setWorkingLastRootCanalAge(e.target.value)
              }
            }}
            onBlur={() => setLastRootCanalAge(workingLastRootCanalAge)}
            borderColor={rootCanalError ? 'red' : undefined}
            borderWidth={rootCanalError ? '2px' : undefined}
            disabled={isFieldDisabled(lastRootCanalAge)}
          />
        </Flex>
        <Flex>
          <FormLabel w="50%">
            <em>Number of years exposed</em>
          </FormLabel>
          <Text
            w="25%"
            fontWeight="bold"
            color={rootCanalError ? 'red' : undefined}
            pl={3}
          >
            {computedRootYearsExposed || '-'}
          </Text>
        </Flex>
        <Flex>
          <FormLabel w="50%">
            <em>Estimated root canal exposure</em>
          </FormLabel>
          <Text w="25%" fontWeight="bold" pl={3}>
            {computedRootCanalExposure || '-'}
          </Text>
        </Flex>
      </SimpleGrid>
    </>
  )
}

export default RootCanalsSection
